/**
 * @class
 * @function CollectionBannerSlider
 */

if (!customElements.get('collection-banner-slider')) {
  class CollectionBannerSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isDragging = false;
      this.startPos = 0;
      this.currentTranslate = 0;
      this.prevTranslate = 0;
      this.slideWidth = 0;
      this.dragThreshold = 50; // Minimum drag distance to trigger slide change
    }

    connectedCallback() {
      this.slider = this.querySelector('.collection-banner-slider');
      if (!this.slider) return;

      this.sectionId = this.slider.dataset.sectionId;
      this.track = this.slider.querySelector('.collection-banner-slider__track');
      this.slides = Array.from(this.slider.querySelectorAll('.collection-banner-slider__slide'));
      this.prevBtn = this.slider.querySelector('.banner-prev');
      this.nextBtn = this.slider.querySelector('.banner-next');
      this.dots = Array.from(this.slider.querySelectorAll('.banner-dots .dot'));

      if (this.slides.length <= 1) return;

      this.init();
    }

    init() {
      this.updateSlideWidth();
      this.setupEventListeners();
      this.updateUI();

      // Debounced resize handler
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.updateSlideWidth();
          this.goToSlide(this.currentIndex, false);
        }, 150);
      });
    }

    updateSlideWidth() {
      this.slideWidth = this.slider.offsetWidth;
    }

    setupEventListeners() {
      // Touch events
      this.track.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
      this.track.addEventListener('touchmove', this.touchMove.bind(this), { passive: false });
      this.track.addEventListener('touchend', this.touchEnd.bind(this), { passive: true });

      // Mouse events
      this.track.addEventListener('mousedown', this.touchStart.bind(this));
      this.track.addEventListener('mousemove', this.touchMove.bind(this));
      this.track.addEventListener('mouseup', this.touchEnd.bind(this));
      this.track.addEventListener('mouseleave', this.touchEnd.bind(this));

      // Prevent context menu on long press
      this.track.addEventListener('contextmenu', (e) => {
        if (this.isDragging) e.preventDefault();
      });

      // Prevent image dragging
      this.track.addEventListener('dragstart', (e) => e.preventDefault());

      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Dots
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      // Shopify theme editor support
      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const blockId = event.detail.blockId;
          const slideIndex = this.slides.findIndex(
            slide => slide.dataset.blockId === blockId
          );
          if (slideIndex !== -1) {
            this.goToSlide(slideIndex);
          }
        });
      }
    }

    touchStart(e) {
      this.isDragging = true;
      this.startPos = this.getPositionX(e);
      this.track.classList.add('is-dragging');
      
      // Disable transition during drag
      this.track.style.transition = 'none';
    }

    touchMove(e) {
      if (!this.isDragging) return;
      
      // Prevent default only for touch to allow scrolling on desktop
      if (e.type === 'touchmove') {
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - this.startPos);
        const deltaY = Math.abs(touch.clientY - (this.startY || touch.clientY));
        
        // Only prevent if horizontal swipe is dominant
        if (deltaX > deltaY) {
          e.preventDefault();
        }
      }
      
      const currentPosition = this.getPositionX(e);
      const movement = currentPosition - this.startPos;
      
      // Add resistance at boundaries
      let resistance = 1;
      if ((this.currentIndex === 0 && movement > 0) || 
          (this.currentIndex === this.slides.length - 1 && movement < 0)) {
        resistance = 0.3;
      }
      
      this.currentTranslate = this.prevTranslate + (movement * resistance);
      
      // Use transform for smooth dragging
      requestAnimationFrame(() => {
        this.track.style.transform = `translateX(${this.currentTranslate}px)`;
      });
    }

    touchEnd() {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      
      // Re-enable transition
      this.track.style.transition = '';

      const movedBy = this.currentTranslate - this.prevTranslate;

      // Determine if we should go to next/prev slide
      if (movedBy < -this.dragThreshold && this.currentIndex < this.slides.length - 1) {
        this.currentIndex++;
      } else if (movedBy > this.dragThreshold && this.currentIndex > 0) {
        this.currentIndex--;
      }

      this.goToSlide(this.currentIndex);
    }

    getPositionX(e) {
      return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    }

    goToSlide(index, animate = true) {
      this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.currentTranslate = -this.currentIndex * this.slideWidth;
      this.prevTranslate = this.currentTranslate;

      if (!animate) {
        this.track.style.transition = 'none';
      } else {
        this.track.style.transition = '';
      }
      
      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
      this.updateUI();
      
      // Sync after animation starts
      requestAnimationFrame(() => {
        this.syncCollectionTabs();
      });

      if (!animate) {
        // Force reflow
        requestAnimationFrame(() => {
          this.track.offsetHeight;
          this.track.style.transition = '';
        });
      }
    }

    prev() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    next() {
      if (this.currentIndex < this.slides.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    updateUI() {
      // Update dots
      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex) {
          dot.classList.add('is-selected');
        } else {
          dot.classList.remove('is-selected');
        }
      });

      // Update navigation buttons
      if (this.prevBtn) {
        if (this.currentIndex === 0) {
          this.prevBtn.classList.add('is-disabled');
        } else {
          this.prevBtn.classList.remove('is-disabled');
        }
      }

      if (this.nextBtn) {
        if (this.currentIndex === this.slides.length - 1) {
          this.nextBtn.classList.add('is-disabled');
        } else {
          this.nextBtn.classList.remove('is-disabled');
        }
      }
    }

    syncCollectionTabs() {
      // Get the current slide's block ID
      const currentSlide = this.slides[this.currentIndex];
      if (!currentSlide) return;

      const blockId = currentSlide.dataset.blockId;
      const collectionHandle = `collection-${blockId}`;

      // Find the collection tabs component
      const section = this.closest('.collection-tabs');
      if (!section) return;

      const productGrid = section.querySelector(`[data-id="${this.sectionId}"]`);
      if (!productGrid) return;

      const themeScroll = section.querySelector('theme-scroll');
      if (!themeScroll) return;

      // Hide all products
      const allProducts = productGrid.querySelectorAll('.collection-tabs--product');
      allProducts.forEach(product => {
        product.classList.remove('carousel__slide');
      });

      // Show products for current collection
      const activeProducts = productGrid.querySelectorAll(`[data-collection="${collectionHandle}"]`);
      activeProducts.forEach(product => {
        product.classList.add('carousel__slide');
      });

      // Reinitialize and scroll to top
      if (themeScroll.init) {
        themeScroll.init();
      }
      
      const scrollContainer = themeScroll.querySelector('.theme-scroll--inner');
      if (scrollContainer) {
        scrollContainer.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }

      // Update tab buttons if they exist
      const sectionHeader = section.querySelector('.section-header');
      if (sectionHeader) {
        const tabButtons = sectionHeader.querySelectorAll('button');
        const tabLinks = sectionHeader.querySelectorAll('.linked-to-tab');
        
        tabButtons.forEach((btn, index) => {
          if (index === this.currentIndex) {
            btn.classList.add('active');
          } else {
            btn.classList.remove('active');
          }
        });

        tabLinks.forEach((link, index) => {
          if (index === this.currentIndex) {
            link.classList.add('active');
          } else {
            link.classList.remove('active');
          }
        });
      }
    }
  }

  customElements.define('collection-banner-slider', CollectionBannerSlider);
}